// マスタCSVインポートのポップアップを構成する表示部品。
// 判断や整形は masterCsvDialogState が済ませた状態で受け取り、ここでは並べるだけにする。
import { useRef, useState } from "react";
import {
  Alert, Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import type { MasterImportCollision, MasterImportIdPolicy, MasterImportRename } from "../../../schema/console/master_import";
import type {
  MasterCsvCollisionRow, MasterCsvErrorView, MasterCsvImportTargetRow, MasterCsvRenameRow, MasterCsvRenameSummary,
} from "./csv/masterCsvDialogState";
import type { MasterCsvTable } from "./csv/masterCsvFiles";
import {
  MASTER_IMPORT_COLLISION_COUNT_HEADER_LABEL, MASTER_IMPORT_DROP_AREA_MESSAGE, MASTER_IMPORT_ERROR_COLUMN_HEADER_LABEL,
  MASTER_IMPORT_ERROR_MESSAGE_HEADER_LABEL, MASTER_IMPORT_ERROR_SOURCE_HEADER_LABEL, MASTER_IMPORT_FILE_INPUT_ARIA_LABEL,
  MASTER_IMPORT_LINE_NUMBER_HEADER_LABEL, MASTER_IMPORT_LINE_NUMBER_HEADER_NOTE, MASTER_IMPORT_RENAME_COLUMN_HEADER_LABEL,
  MASTER_IMPORT_RENAME_FROM_HEADER_LABEL, MASTER_IMPORT_RENAME_LINE_NUMBER_HEADER_LABEL, MASTER_IMPORT_RENAME_TO_HEADER_LABEL,
  MASTER_IMPORT_SOURCE_HEADER_LABEL, hiddenErrorCountTextOf, idCollisionNoticeTextOf, importTargetRowsOf,
  renamePreviewHeadlineTextOf, renameRowKeyOf, renameRowsOf, renameSummaryOf, renameSummaryTextOf,
} from "./csv/masterCsvDialogState";

// 受け取ったファイル一覧を配列にする。ファイル選択と投げ込みで形が違うため、ここで揃える。
function filesOfList(fileList: FileList | null): File[] {
  if (fileList === null) {
    return [];
  }
  return Array.from(fileList);
}

// 投げ込み中だと分かるよう、受け付ける状態のときだけ枠と背景の色を変える。
function dropAreaColorsOf(isDragging: boolean): { borderColor: string; backgroundColor: string } {
  if (isDragging) {
    return { borderColor: "primary.main", backgroundColor: "action.hover" };
  }
  return { borderColor: "divider", backgroundColor: "transparent" };
}

export type MasterCsvDropAreaProps = {
  onFilesSelected: (files: File[]) => void;
};

// ファイルを投げ込む領域と、ファイル選択ボタン。投げ込み中は枠の色を変えて受け付ける状態だと分かるようにする。
export function MasterCsvDropArea({ onFilesSelected }: MasterCsvDropAreaProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    onFilesSelected(filesOfList(event.dataTransfer.files));
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = filesOfList(event.target.files);
    // 同じファイルを選び直しても変更として扱われるよう、選択内容を空に戻す。
    event.target.value = "";
    onFilesSelected(files);
  };

  const { borderColor, backgroundColor } = dropAreaColorsOf(isDragging);

  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        border: "2px dashed",
        borderColor,
        backgroundColor,
        borderRadius: 1,
        px: 2,
        py: 3,
        textAlign: "center",
      }}
    >
      <Stack alignItems="center" gap={1}>
        <UploadFileIcon color="action" />
        <Typography variant="body2" color="text.secondary">
          {MASTER_IMPORT_DROP_AREA_MESSAGE}
        </Typography>
        <Button
          startIcon={<UploadFileIcon />}
          variant="outlined"
          size="small"
          onClick={() => fileInputRef.current?.click()}
        >
          ファイルを選択
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.zip,text/csv,application/zip"
          multiple
          hidden
          aria-label={MASTER_IMPORT_FILE_INPUT_ARIA_LABEL}
          onChange={onInputChange}
        />
      </Stack>
    </Box>
  );
}

export type MasterCsvErrorTableProps = { view: MasterCsvErrorView };

// 不備の一覧。ファイル名・行番号・列名・理由の4列で示し、特定できない欄は空にする。
export function MasterCsvErrorTable({ view }: MasterCsvErrorTableProps) {
  const hiddenCountText = hiddenErrorCountTextOf(view.hiddenCount);
  return (
    <Stack gap={1}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{MASTER_IMPORT_ERROR_SOURCE_HEADER_LABEL}</TableCell>
            <TableCell>
              {MASTER_IMPORT_LINE_NUMBER_HEADER_LABEL}
              <Typography variant="caption" component="div" color="text.secondary">{MASTER_IMPORT_LINE_NUMBER_HEADER_NOTE}</Typography>
            </TableCell>
            <TableCell>{MASTER_IMPORT_ERROR_COLUMN_HEADER_LABEL}</TableCell>
            <TableCell>{MASTER_IMPORT_ERROR_MESSAGE_HEADER_LABEL}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {view.rows.map((row, index) => (
            <TableRow key={`${row.source}-${row.lineNumber}-${row.columnName}-${index}`}>
              <TableCell>{row.source}</TableCell>
              <TableCell>{row.lineNumber}</TableCell>
              <TableCell>{row.columnName}</TableCell>
              <TableCell>{row.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hiddenCountText !== "" && (
        <Typography variant="body2" color="text.secondary">{hiddenCountText}</Typography>
      )}
    </Stack>
  );
}

export type MasterCsvImportTargetListProps = { rows: readonly MasterCsvImportTargetRow[] };

// 取り込む対象になったマスタと行数の一覧。取り込む前に規模を把握できるようにする。
export function MasterCsvImportTargetList({ rows }: MasterCsvImportTargetListProps) {
  return (
    <Stack gap={0.25}>
      {rows.map((row) => (
        <Typography key={row.collectionKey} variant="body2">{row.text}</Typography>
      ))}
    </Stack>
  );
}

export type MasterCsvRenameSummaryTextProps = { summary: MasterCsvRenameSummary; idPolicy: MasterImportIdPolicy };

// 取り込む行の件数の内訳。IDの決め方によって、追加になる行と上書きになる行の書き分けが変わる。
// 文を1つ並べるだけの薄い包みだが、確認画面の他の一覧部品と同じ呼び出し方で並べられるよう部品のままにする。
export function MasterCsvRenameSummaryText({ summary, idPolicy }: MasterCsvRenameSummaryTextProps) {
  return <Typography variant="body2">{renameSummaryTextOf(summary, idPolicy)}</Typography>;
}

export type MasterCsvCollisionListProps = { rows: readonly MasterCsvCollisionRow[] };

// ID が既存の行と重なったテーブルと件数の一覧。
// 1つの画面に複数のテーブルが属することがあるため、マスタ名には元のファイル名が併記されている。
export function MasterCsvCollisionList({ rows }: MasterCsvCollisionListProps) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{MASTER_IMPORT_SOURCE_HEADER_LABEL}</TableCell>
          <TableCell>{MASTER_IMPORT_COLLISION_COUNT_HEADER_LABEL}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.collectionKey}>
            <TableCell>{row.label}</TableCell>
            <TableCell>{`${row.count}件`}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export type MasterCsvRenameListProps = { rows: readonly MasterCsvRenameRow[] };

// 値が書き換わる予定の行の一覧。
// IDをそのまま使う取り込みでは、値が書き換わった行が新しく増えるとは限らず、
// IDが重なっていれば同じIDの既存の行を置き換えることになる。
// 書き換わる項目はマスタによって違い、工事マスタでは名前ではなく整理番号が変わる。
// どの項目が変わるのかを行ごとに示し、変更前と変更後を並べて見せる。
export function MasterCsvRenameList({ rows }: MasterCsvRenameListProps) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{MASTER_IMPORT_SOURCE_HEADER_LABEL}</TableCell>
          <TableCell>{MASTER_IMPORT_RENAME_LINE_NUMBER_HEADER_LABEL}</TableCell>
          <TableCell>{MASTER_IMPORT_RENAME_COLUMN_HEADER_LABEL}</TableCell>
          <TableCell>{MASTER_IMPORT_RENAME_FROM_HEADER_LABEL}</TableCell>
          <TableCell>{MASTER_IMPORT_RENAME_TO_HEADER_LABEL}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={renameRowKeyOf(row)}>
            <TableCell>{row.label}</TableCell>
            <TableCell>{row.lineNumber}</TableCell>
            <TableCell>{row.columnLabel}</TableCell>
            <TableCell>{row.fromValue}</TableCell>
            <TableCell>{row.toValue}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export type MasterCsvPlanReviewProps = {
  tables: readonly MasterCsvTable[];
  collisions: readonly MasterImportCollision[];
  idPolicy: MasterImportIdPolicy;
  renames: readonly MasterImportRename[];
};

// 取り込む内容を確かめてもらう一覧。承認前の確認と、承認が食い違ったあとのやり直しで同じ内容を出す。
// 名前の変更が1件も無いことがあり、そのときは見出しと変更一覧を出さない。
// 取り込む行数と上書きされる行数は名前の変更の有無にかかわらず確かめてもらう必要があるため、常に出す。
export function MasterCsvPlanReview({ tables, collisions, idPolicy, renames }: MasterCsvPlanReviewProps) {
  const idCollisionNoticeText = idCollisionNoticeTextOf(collisions, idPolicy);
  const hasRenames = renames.length > 0;
  return (
    <>
      {hasRenames && (
        <Alert severity="warning">{renamePreviewHeadlineTextOf(collisions, idPolicy)}</Alert>
      )}
      {idCollisionNoticeText !== "" && (
        <Typography variant="body2" color="warning.main">{idCollisionNoticeText}</Typography>
      )}
      <MasterCsvImportTargetList rows={importTargetRowsOf(tables, collisions, idPolicy)} />
      <MasterCsvRenameSummaryText summary={renameSummaryOf(tables, renames, collisions, idPolicy)} idPolicy={idPolicy} />
      {hasRenames && (
        <MasterCsvRenameList rows={renameRowsOf(renames, tables)} />
      )}
    </>
  );
}
